import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Usb, Download, Check, AlertCircle, RefreshCw, Settings, TestTube, Wifi, ChevronRight, Copy, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as printerApi from '@/services/printerApi';
import apiClient from '@/services/apiClient';

const PrinterSetup = () => {
  const navigate = useNavigate();
  
  // Wizard step
  const [currentStep, setCurrentStep] = useState(1);
  
  // Printer config
  const [printerConfig, setPrinterConfig] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('tspl');
  const [selectedLabelType, setSelectedLabelType] = useState('usn');
  const [testCode, setTestCode] = useState('');
  
  // Test result
  const [testResult, setTestResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  
  // USB device
  const [usbDevice, setUsbDevice] = useState(null);
  const [usbSupported, setUsbSupported] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  
  // Copied state
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadPrinterConfig();
    setUsbSupported(printerApi.isWebUSBSupported());
  }, []);

  const loadPrinterConfig = async () => {
    setIsLoading(true);
    try {
      const config = await printerApi.getPrinterConfig();
      setPrinterConfig(config);
    } catch (error) {
      console.error('Error loading printer config:', error);
      toast.error('Failed to load printer configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateTestPrint = async () => {
    setIsTesting(true);
    try {
      const response = await apiClient.post('/printer/test-print', {
        language: selectedLanguage,
        label_type: selectedLabelType,
        test_code: testCode || null
      });
      
      setTestResult(response.data);
      toast.success('Test print generated successfully');
      setCurrentStep(3);
    } catch (error) {
      console.error('Error generating test print:', error);
      toast.error('Failed to generate test print');
    } finally {
      setIsTesting(false);
    }
  };

  const handleConnectUSB = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'}`);
    } catch (error) {
      console.error('Error connecting USB:', error);
      toast.error(error.message || 'Failed to connect USB printer');
    }
  };

  const handlePrintViaUSB = async () => {
    if (!testResult?.commands) {
      toast.error('No print commands available');
      return;
    }

    setIsPrinting(true);
    try {
      if (usbDevice) {
        await printerApi.sendToUSBPrinter(usbDevice, testResult.commands);
      } else {
        // Request device and print
        const device = await printerApi.requestUSBPrinter();
        setUsbDevice(device);
        await printerApi.sendToUSBPrinter(device, testResult.commands);
      }
      toast.success('Test label sent to printer!');
    } catch (error) {
      console.error('Error printing:', error);
      // Show detailed error with line breaks
      const errorMessage = error.message || 'Failed to print';
      if (errorMessage.includes('\n')) {
        // Multi-line error - show as detailed toast
        toast.error(
          <div className="space-y-1">
            {errorMessage.split('\n').map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDownloadCommands = () => {
    if (!testResult?.commands) return;
    
    const extMap = { zpl: 'zpl', tspl: 'prn', escpos: 'bin' };
    const ext = extMap[selectedLanguage] || 'txt';
    const filename = `test_print_${selectedLabelType}.${ext}`;
    
    const blob = new Blob([testResult.commands], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Print file downloaded');
  };

  const handleCopyCommands = () => {
    if (!testResult?.commands) return;
    navigator.clipboard.writeText(testResult.commands);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Commands copied to clipboard');
  };

  const getLabelSize = () => {
    const sizes = {
      usn: '33mm × 26mm',
      bundle: '33mm × 26mm',
      document: '50mm × 35mm'
    };
    return sizes[selectedLabelType] || '33mm × 26mm';
  };

  const steps = [
    { number: 1, title: 'Select Printer', icon: Settings },
    { number: 2, title: 'Configure Label', icon: TestTube },
    { number: 3, title: 'Test Print', icon: Printer }
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Printer className="w-6 h-6" />
            <div>
              <h1 className="text-xl font-bold font-display">Printer Setup Wizard</h1>
              <p className="text-xs text-[#E8DCC8]">Configure and test your thermal printer</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {/* Step Progress */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((step, idx) => (
            <React.Fragment key={step.number}>
              <div 
                className={`flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-all ${
                  currentStep === step.number 
                    ? 'bg-[#2D5F4F] text-white' 
                    : currentStep > step.number 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                }`}
                onClick={() => step.number < currentStep && setCurrentStep(step.number)}
              >
                {currentStep > step.number ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <step.icon className="w-5 h-5" />
                )}
                <span className="font-medium hidden sm:inline">{step.title}</span>
              </div>
              {idx < steps.length - 1 && (
                <ChevronRight className="w-5 h-5 text-gray-400 mx-2" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Printer Language */}
        {currentStep === 1 && (
          <Card className="p-6 bg-white border border-[#E5DFD0]">
            <h2 className="text-xl font-semibold text-[#1A1F2E] mb-6">Step 1: Select Your Printer Type</h2>
            
            {isLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#2D5F4F]" />
                <p className="text-gray-500 mt-2">Loading printer configuration...</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    Select Printer Language
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {printerConfig?.supported_languages?.map((lang) => (
                      <div
                        key={lang.code}
                        onClick={() => setSelectedLanguage(lang.code)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedLanguage === lang.code
                            ? 'border-[#2D5F4F] bg-[#2D5F4F]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            selectedLanguage === lang.code ? 'border-[#2D5F4F]' : 'border-gray-300'
                          }`}>
                            {selectedLanguage === lang.code && (
                              <div className="w-2 h-2 rounded-full bg-[#2D5F4F]" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-[#1A1F2E]">{lang.name}</p>
                            <p className="text-xs text-gray-500">{lang.description}</p>
                          </div>
                        </div>
                        {lang.default && (
                          <span className="mt-2 inline-block text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            Recommended for TSC
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {usbSupported ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Web USB Supported</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Your browser supports direct USB printing. You can print directly without additional software.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">Web USB Not Supported</span>
                    </div>
                    <p className="text-sm text-orange-600 mt-1">
                      Use Chrome or Edge browser for direct USB printing. You can still download print files.
                    </p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button 
                    onClick={() => setCurrentStep(2)}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  >
                    Next: Configure Label
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Step 2: Configure Label */}
        {currentStep === 2 && (
          <Card className="p-6 bg-white border border-[#E5DFD0]">
            <h2 className="text-xl font-semibold text-[#1A1F2E] mb-6">Step 2: Configure Test Label</h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  Select Label Type
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { id: 'usn', name: 'USN Sticker', size: '33mm × 26mm', desc: 'Product USN codes' },
                    { id: 'bundle', name: 'Bundle Label', size: '33mm × 26mm', desc: 'Bundle identification' },
                    { id: 'document', name: 'Document Label', size: '50mm × 35mm', desc: 'Document barcodes' }
                  ].map((label) => (
                    <div
                      key={label.id}
                      onClick={() => setSelectedLabelType(label.id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedLabelType === label.id
                          ? 'border-[#2D5F4F] bg-[#2D5F4F]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                          selectedLabelType === label.id ? 'border-[#2D5F4F]' : 'border-gray-300'
                        }`}>
                          {selectedLabelType === label.id && (
                            <div className="w-2 h-2 rounded-full bg-[#2D5F4F]" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-[#1A1F2E]">{label.name}</p>
                          <p className="text-sm text-[#2D5F4F] font-medium">{label.size}</p>
                          <p className="text-xs text-gray-500">{label.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  Test Code (Optional)
                </Label>
                <Input
                  value={testCode}
                  onChange={(e) => setTestCode(e.target.value)}
                  placeholder={`e.g., TEST-${selectedLabelType.toUpperCase()}-001`}
                  className="max-w-md"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use default test code
                </p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2">Label Preview</h3>
                <div className="flex items-center gap-4">
                  <div 
                    className="bg-white border-2 border-blue-300 rounded flex items-center justify-center"
                    style={{ 
                      width: selectedLabelType === 'document' ? '100px' : '66px',
                      height: selectedLabelType === 'document' ? '70px' : '52px'
                    }}
                  >
                    <div className="text-center">
                      <div className="w-8 h-8 bg-gray-200 mx-auto mb-1 rounded" />
                      <span className="text-[8px] text-gray-600">QR + Text</span>
                    </div>
                  </div>
                  <div className="text-sm">
                    <p><strong>Size:</strong> {getLabelSize()}</p>
                    <p><strong>Content:</strong> QR Code + Text</p>
                    <p><strong>Language:</strong> {selectedLanguage.toUpperCase()}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button 
                  onClick={() => setCurrentStep(1)}
                  variant="outline"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleGenerateTestPrint}
                  disabled={isTesting}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                >
                  {isTesting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Generate Test Print
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Test Print */}
        {currentStep === 3 && testResult && (
          <Card className="p-6 bg-white border border-[#E5DFD0]">
            <h2 className="text-xl font-semibold text-[#1A1F2E] mb-6">Step 3: Send Test Print</h2>
            
            <div className="space-y-6">
              {/* Test Print Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Test Code</p>
                  <p className="font-mono font-semibold text-[#1A1F2E]">{testResult.test_code}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Label Type</p>
                  <p className="font-semibold text-[#1A1F2E] capitalize">{testResult.label_type}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Label Size</p>
                  <p className="font-semibold text-[#1A1F2E]">{testResult.label_size}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Language</p>
                  <p className="font-semibold text-[#1A1F2E] uppercase">{testResult.language}</p>
                </div>
              </div>

              {/* Preview */}
              {testResult.preview && (
                <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
                  <img 
                    src={testResult.preview.qr_image} 
                    alt="QR Preview" 
                    className="w-24 h-24 border border-gray-300 rounded"
                  />
                  <div>
                    <p className="font-medium text-[#1A1F2E]">Label Preview</p>
                    <p className="text-sm text-gray-600 mt-1">
                      QR Code contains: <code className="bg-white px-2 py-0.5 rounded">{testResult.test_code}</code>
                    </p>
                  </div>
                </div>
              )}

              {/* Print Options */}
              <div className="space-y-4">
                <h3 className="font-medium text-[#1A1F2E]">Choose Print Method:</h3>
                
                <Tabs defaultValue="usb" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="usb" disabled={!usbSupported}>
                      <Usb className="w-4 h-4 mr-2" />
                      USB Direct
                    </TabsTrigger>
                    <TabsTrigger value="download">
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </TabsTrigger>
                    <TabsTrigger value="commands">
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Commands
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="usb" className="mt-4">
                    {usbSupported ? (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Connect your USB thermal printer and click the button below to send the test print directly.
                        </p>
                        {usbDevice && (
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-600" />
                            <span className="text-green-700">
                              Connected to: <strong>{usbDevice.productName || 'USB Printer'}</strong>
                            </span>
                          </div>
                        )}
                        <div className="flex gap-3">
                          {!usbDevice && (
                            <Button onClick={handleConnectUSB} variant="outline">
                              <Usb className="w-4 h-4 mr-2" />
                              Connect Printer
                            </Button>
                          )}
                          <Button 
                            onClick={handlePrintViaUSB}
                            disabled={isPrinting}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {isPrinting ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Printer className="w-4 h-4 mr-2" />
                            )}
                            Print Test Label
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-orange-700">
                          USB printing is not supported in your browser. Please use Chrome or Edge, or use the Download/Copy methods.
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="download" className="mt-4">
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Download the print commands as a file. You can send this file to your printer using:
                      </p>
                      <ul className="text-sm text-gray-600 list-disc ml-5 space-y-1">
                        <li>TSC BarTender software</li>
                        <li>Command prompt: <code className="bg-gray-100 px-1 rounded">copy /b file.prn LPT1</code></li>
                        <li>Any printer utility that accepts raw commands</li>
                      </ul>
                      <Button onClick={handleDownloadCommands} className="bg-blue-600 hover:bg-blue-700">
                        <Download className="w-4 h-4 mr-2" />
                        Download Print File
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="commands" className="mt-4">
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Copy the raw printer commands and paste them into your printer&apos;s terminal or send via serial/TCP connection.
                      </p>
                      <div className="relative">
                        <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-48 font-mono">
                          {testResult.commands}
                        </pre>
                        <Button 
                          onClick={handleCopyCommands}
                          size="sm"
                          className="absolute top-2 right-2"
                          variant="secondary"
                        >
                          {copied ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3 mr-1" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Success Checklist */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-medium text-green-800 mb-2">After Printing, Verify:</h3>
                <ul className="text-sm text-green-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    QR code is scannable with a barcode scanner
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Text is readable and correctly aligned
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Label size matches your sticker paper
                  </li>
                </ul>
              </div>

              <div className="flex justify-between">
                <Button 
                  onClick={() => setCurrentStep(2)}
                  variant="outline"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Configure
                </Button>
                <Button 
                  onClick={() => {
                    toast.success('Printer setup complete!');
                    navigate('/dashboard');
                  }}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Finish Setup
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Help Section */}
        <Card className="mt-6 p-4 bg-white border border-[#E5DFD0]">
          <h3 className="font-medium text-[#1A1F2E] mb-2">Troubleshooting Tips</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>• <strong>TSC Printers:</strong> Use TSPL language (default). Most TSC models also support ZPL mode.</li>
            <li>• <strong>Zebra Printers:</strong> Use ZPL language for best compatibility.</li>
            <li>• <strong>USB Not Detecting:</strong> Make sure printer is powered on and connected via USB. Try a different USB port.</li>
            <li>• <strong>Label Size Mismatch:</strong> Ensure your printer is loaded with the correct label size paper.</li>
            <li>• <strong>QR Code Not Scanning:</strong> Try increasing the cell size or check printer resolution (DPI) settings.</li>
            <li className="pt-2 border-t border-gray-200">
              <strong className="text-red-600">USB "Access Denied" Error:</strong>
              <ul className="ml-4 mt-1 space-y-1 text-gray-500">
                <li>1. Close all printer software (BarTender, TSC Diagnostic Tool, etc.)</li>
                <li>2. Unplug the USB cable, wait 5 seconds, and plug it back in</li>
                <li>3. If issue persists, use the <strong>"Download File"</strong> method instead</li>
                <li>4. On Windows, you may need to install WinUSB driver using Zadig tool</li>
              </ul>
            </li>
          </ul>
        </Card>

        {/* Alternative Method Highlight */}
        <Card className="mt-4 p-4 bg-blue-50 border border-blue-200">
          <h3 className="font-medium text-blue-800 mb-2">Recommended: Download File Method</h3>
          <p className="text-sm text-blue-700">
            If USB Direct printing fails, use the <strong>"Download File"</strong> option and send the file to your printer using:
          </p>
          <ul className="text-sm text-blue-600 mt-2 space-y-1 ml-4">
            <li>• Windows Command Prompt: <code className="bg-white px-1 rounded">copy /b filename.prn \\COMPUTERNAME\PrinterShareName</code></li>
            <li>• Or simply double-click the downloaded .prn file if your printer is set as default</li>
          </ul>
        </Card>
      </main>
    </div>
  );
};

export default PrinterSetup;
